import React, { CSSProperties, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import { AlertIcon, CrossmarkIcon } from 'assets/icons';
import { JustifyContentType } from 'common/types/flex';
import {
  CrossMarkIconWrapper,
  Description,
  RandomlyPositionedModal,
} from 'components/common/CustomModal/CustomModal.styled';
import { DefaultButton } from 'components/styled/Button';
import { Flex } from 'components/styled/Flex';
import { Backdrop } from 'components/styled/SidebarModal';

import { Title } from '../DeleteModal/DeleteModal.styled';

interface IWarningModal {
  title: string;
  description?: string;
  cancelLabel?: string;
  confirmLabel?: string;
  icon?: ReactNode;
  onCancel?: () => void;
  onConfirm?: () => void;
  children?: ReactNode;
  onClickCrossmarkIcon?: () => void;
  additionalStyle?: CSSProperties;
  isConfirmLoading?: boolean;
  isConfirmDisabled?: boolean;
  closeOnConfirm?: boolean;
  footerJustifyContent?: JustifyContentType;
}

export const WarningModal = ({
  title,
  description,
  onCancel,
  cancelLabel,
  confirmLabel,
  icon,
  onConfirm,
  children,
  onClickCrossmarkIcon,
  additionalStyle,
  isConfirmLoading,
  isConfirmDisabled,
  closeOnConfirm = true,
  footerJustifyContent = 'space-between',
}: IWarningModal) => {
  const [show, setShow] = useState(true);
  const { t } = useTranslation();
  const { colors } = useTheme();
  const renderBackdrop = (props: any) => <Backdrop {...props} />;

  const onClickCrossmarkIconHandler = () => onClickCrossmarkIcon && onClickCrossmarkIcon();

  const discardChanges = () => {
    if (onCancel) {
      setShow(false);
      onCancel();
    }
  };

  const saveData = () => {
    if (onConfirm) {
      if (closeOnConfirm) setShow(false);
      onConfirm();
    }
  };

  return (
    <RandomlyPositionedModal
      show={show}
      onHide={onClickCrossmarkIcon ? onClickCrossmarkIconHandler : discardChanges}
      renderBackdrop={renderBackdrop}
      aria-labelledby="modal-label"
      additionalStyle={additionalStyle}
    >
      <>
        <Flex justifyContent="space-between" margin="0 0 1.5rem 0">
          <Flex gap="0.5rem" alignItems="center">
            {icon ? (
              icon
            ) : (
              <AlertIcon stroke={colors.warning_dark} width={24} height={24} strokeWidth={1.5} />
            )}
            <Title>{title}</Title>
          </Flex>
          <CrossMarkIconWrapper
            onClick={onClickCrossmarkIcon ? onClickCrossmarkIconHandler : discardChanges}
          >
            <CrossmarkIcon />
          </CrossMarkIconWrapper>
        </Flex>

        <Description>{description}</Description>
        {children}
        <Flex justifyContent={footerJustifyContent} margin={children ? '0' : '2rem 0 0 0'}>
          {onCancel && (
            <DefaultButton type="button" color="white" onClick={() => discardChanges()}>
              {cancelLabel || t('Cancel')}
            </DefaultButton>
          )}

          {onConfirm && (
            <DefaultButton
              type="submit"
              color="blue"
              margin="0 0 0 1rem"
              disabled={isConfirmLoading || isConfirmDisabled}
              onClick={() => saveData()}
            >
              {confirmLabel || t('Continue')}
            </DefaultButton>
          )}
        </Flex>
      </>
    </RandomlyPositionedModal>
  );
};
